export const BULK_APPLY_TIMELINE_MESSAGE = "Bulk apply timeline";

export type BulkApplySpanDetail = Record<string, number | string | boolean>;

export interface BulkApplyTimelineSpan {
  span: string;
  startedAtMs: number;
  durationMs: number;
  detail?: BulkApplySpanDetail;
}

export interface BulkApplyRunTimeline {
  spans: BulkApplyTimelineSpan[];
  totalsBySpanMs: Record<string, number>;
  countsBySpan: Record<string, number>;
  longestBySpanMs: Record<string, number>;
  runDurationMs: number;
}

declare global {
  interface Window {
    __bulkApplyTimeline?: BulkApplyRunTimeline;
  }
}

export interface BulkApplyTimelineRun {
  startedAtMs: number;
  endedAtMs: number | null;
  spans: BulkApplyTimelineSpan[];
}

let activeTimelineRun: BulkApplyTimelineRun | null = null;

const nowMs = (): number => performance.now();

const endTimelineRun = (timelineRun: BulkApplyTimelineRun): void => {
  timelineRun.endedAtMs ??= nowMs();
};

export const beginBulkApplyTimeline = (): BulkApplyTimelineRun => {
  if (activeTimelineRun !== null) endTimelineRun(activeTimelineRun);
  const timelineRun: BulkApplyTimelineRun = { startedAtMs: nowMs(), endedAtMs: null, spans: [] };
  activeTimelineRun = timelineRun;
  return timelineRun;
};

export const recordBulkApplySpan = (span: string, spanStartedAtMs: number, detail?: BulkApplySpanDetail): void => {
  if (activeTimelineRun === null) return;
  if (spanStartedAtMs < activeTimelineRun.startedAtMs) return;
  activeTimelineRun.spans.push({
    span,
    startedAtMs: Math.round(spanStartedAtMs - activeTimelineRun.startedAtMs),
    durationMs: Math.round(nowMs() - spanStartedAtMs),
    ...(detail === undefined ? {} : { detail }),
  });
};

export const bulkApplyClockMs = nowMs;

export const timedBulkApplySpan = async <Result>(span: string, run: () => Promise<Result>, detail?: BulkApplySpanDetail): Promise<Result> => {
  const spanStartedAtMs = nowMs();
  try {
    return await run();
  } finally {
    recordBulkApplySpan(span, spanStartedAtMs, detail);
  }
};

export const timedBulkApplySpanSync = <Result>(span: string, run: () => Result, detail?: BulkApplySpanDetail): Result => {
  const spanStartedAtMs = nowMs();
  try {
    return run();
  } finally {
    recordBulkApplySpan(span, spanStartedAtMs, detail);
  }
};

export const bulkApplyTimelineOf = (timelineSpans: BulkApplyTimelineSpan[], runDurationMs: number): BulkApplyRunTimeline => {
  const totalsBySpanMs: Record<string, number> = {};
  const countsBySpan: Record<string, number> = {};
  const longestBySpanMs: Record<string, number> = {};
  for (const { span, durationMs } of timelineSpans) {
    totalsBySpanMs[span] = (totalsBySpanMs[span] ?? 0) + durationMs;
    countsBySpan[span] = (countsBySpan[span] ?? 0) + 1;
    longestBySpanMs[span] = Math.max(longestBySpanMs[span] ?? 0, durationMs);
  }
  return { spans: timelineSpans, totalsBySpanMs, countsBySpan, longestBySpanMs, runDurationMs };
};

export const publishBulkApplyTimeline = (timeline: BulkApplyRunTimeline): void => {
  window.__bulkApplyTimeline = timeline;
};

export const snapshotBulkApplyTimeline = (timelineRun: BulkApplyTimelineRun | null = activeTimelineRun): BulkApplyRunTimeline => {
  if (timelineRun === null) return bulkApplyTimelineOf([], 0);
  const timeline = bulkApplyTimelineOf([...timelineRun.spans], Math.round((timelineRun.endedAtMs ?? nowMs()) - timelineRun.startedAtMs));
  if (timelineRun === activeTimelineRun) window.__bulkApplyTimeline = timeline;
  return timeline;
};

export const finishBulkApplyTimeline = (timelineRun: BulkApplyTimelineRun | null = activeTimelineRun): BulkApplyRunTimeline => {
  if (timelineRun === null) return bulkApplyTimelineOf([], 0);
  endTimelineRun(timelineRun);
  const timeline = snapshotBulkApplyTimeline(timelineRun);
  if (timelineRun === activeTimelineRun) activeTimelineRun = null;
  return timeline;
};

export const bulkApplyTimelineProperties = (timeline: BulkApplyRunTimeline): Record<string, number> =>
  Object.fromEntries(Object.entries(timeline.totalsBySpanMs).map(([span, totalMs]) => [`stepMs_${span.replace(/[^A-Za-z0-9]+/g, "_")}`, totalMs]));
